import { put, takeEvery, call, select } from "@redux-saga/core/effects";
import { loadUrl, copyHotspotsFromUrl } from "./sagasActions";
import {
  setCurrentFrameIndex,
  setCurrentHotspot,
  setSpinURL,
  setCurrentStep,
  setFilenamesInLayer,
  setUploadFilename,
  setNotification,
} from "..";
import {
  getFolderURLFromSpinURL,
  isLocalStorageAvailable,
  sanitizeHash,
} from "../../util/general";
import { exampleSpinOptions } from "../../config";
import { steps } from "../../util/constants";
import { getFrameCount, getSpinFileName } from "../selectors";
import { setHotspotImportInProgress } from "../hotspotImport/hotspotImport";
import { setStaticImageId } from "../staticImageId/staticImageId";
import { nanoid } from "@reduxjs/toolkit";
import { loadHotspots, loadSpinWorker } from "./loadSpin";
import { fetchSpinInfo } from "../../apis/spinInfo";
import { getDefaultStep } from "../../getDefaultStep";
import { getIsEmbedded } from "../../util/params";
import { requestImageWorker } from "./requestImage";

// ==================WORKERS =========================

function* loadUrlWorker(action) {
  const inputUrl = action.payload;
  const sanitizedURL = sanitizeHash(inputUrl);
  const url = new URL(sanitizedURL);
  const isSpin = url.pathname.trim().slice(-5).toLowerCase() === ".spin";
  if (isSpin) {
    yield loadSpinFromUrlWorker(sanitizedURL);
  } else {
    yield loadStaticImageWorker(sanitizedURL);
  }
  const filename = yield select(getSpinFileName);
  yield put(setUploadFilename(filename));
}

function addToAutocompleteURLList(spinURL) {
  //remember successfully loaded URL for autocompletion
  const isEmbedded = getIsEmbedded();
  if (isEmbedded) {
    return;
  }
  if (!isLocalStorageAvailable()) {
    return;
  }
  let oldUrls = isEmbedded
    ? []
    : new Set(JSON.parse(localStorage.getItem("oldUrls")) ?? []);
  const exampleUrls = exampleSpinOptions.map((x) => x.spinURL);
  if (!exampleUrls.includes(spinURL)) {
    oldUrls.add(spinURL);
    localStorage.setItem("oldUrls", JSON.stringify(Array.from(oldUrls)));
  }
}

function* loadSpinFromUrlWorker(spinURL) {
  try {
    yield put(setCurrentStep("loading"));
    const info = yield call(fetchSpinInfo, spinURL);
    const folderURL = getFolderURLFromSpinURL(spinURL);
    yield loadSpinWorker({ payload: { spinURL, info, folderURL } });
    yield call(addToAutocompleteURLList, spinURL);
  } catch (error) {
    if (error?.response?.status === 404) {
      error.message = `There is no such spin at that URL (404).\nPlease check the URL and try again.`;
    }
    yield put(
      setNotification({
        message:
          error.message || `An error has occurred during loading of ${spinURL}`,
      })
    );
    console.error(error);
    yield put(setCurrentStep(getDefaultStep()));
  }
}

function* loadStaticImageWorker(staticImageUrl) {
  function urlDecode(str) {
    return decodeURIComponent((str + "").replace(/\+/g, "%20"));
  }
  try {
    const filename = urlDecode(staticImageUrl.split("/").pop());
    yield put(setCurrentStep(steps.loading));
    yield put(setFilenamesInLayer({ filenames: [filename] }));
    yield put(setSpinURL(staticImageUrl));
    yield put(setStaticImageId(`im${nanoid(6)}`));
    yield put(setCurrentFrameIndex({ frameIndex: 0 }));
    yield call(requestImageWorker, { payload: { filename, key: "" } });
    yield put(setCurrentStep(steps.preview)); // This triggers the render
  } catch (error) {
    yield put(setCurrentStep(steps.init));
  }
}

function* copyHotspotsFromUrlWorker(action) {
  yield put(setCurrentHotspot(null));
  yield put(setHotspotImportInProgress(true));
  const spinURL = action.payload;
  try {
    const info = yield call(fetchSpinInfo, spinURL);
    const N = yield select(getFrameCount); // frame count
    let loadHotspotsOptions = { loadLocations: true };
    if (N !== Object.keys(info.layers?.[1]).length) {
      loadHotspotsOptions.loadLocations = false;
    }
    // TODO: dispatch action to display a small spinner
    yield loadHotspots(info, loadHotspotsOptions);
    // TODO: dispatch action to hide small spinner and url input field
  } catch (error) {
    // TODO: dispatch action to show error message in the url input field and collapse it
    yield put(
      setNotification({
        message:
          error.message ||
          `An error has occurred during import from  ${spinURL}`,
      })
    );
  } finally {
    yield put(setHotspotImportInProgress(false));
  }
}

// =========== WATCHERS ============
export function* loadUrlWatcher() {
  yield takeEvery(loadUrl().type, loadUrlWorker);
}

export function* copyHotspotsFromUrlWatcher() {
  yield takeEvery(copyHotspotsFromUrl().type, copyHotspotsFromUrlWorker);
}
